import {
  isPartialMaterialCharacteristicsWithShade,
  isPartialMultiShadeCharacteristics,
  type ItemType,
  type MaterialCharacteristics,
} from "@jasper/client-models";
import { useItemMaterialsByItemType } from "./useItemMaterialsByItemType";
import { useMutation } from "@apollo/client/react";
import {
  CreateAnatomyItemDocument,
  DeleteAnatomyItemDocument,
  GetOrderByIdDocument,
  type AnatomyItemCreateInput,
} from "../../gql/generated/graphql";
import { getMultiShadeInfo } from "../../utils";

interface Props {
  productId: string;
  item: {
    type: ItemType;
  };
}

interface CreateItemParams {
  materialCharacteristics?: MaterialCharacteristics;
  teeth: number[];
  itemId?: string;
}

export const useUpsertItem = ({ productId, item }: Props) => {
  const {
    isLoading: isItemMaterialChoicesLoading,
    itemMaterials,
    itemShadeTypes,
    itemShades,
  } = useItemMaterialsByItemType({ itemType: item.type });

  const [createItemMutation, { loading: isCreating }] = useMutation(
    CreateAnatomyItemDocument,
    {
      refetchQueries: [GetOrderByIdDocument],
      awaitRefetchQueries: true,
    }
  );
  const [deleteItemMutation, { loading: isDeleting }] = useMutation(
    DeleteAnatomyItemDocument,
    {
      refetchQueries: [GetOrderByIdDocument],
      awaitRefetchQueries: true,
    }
  );

  const deleteItems = async (itemIds: string[]) => {
    const results = await Promise.allSettled(
      itemIds.map(itemId => deleteItemMutation({ variables: { itemId } }))
    );

    const failures = results.filter(
      (r): r is PromiseRejectedResult => r.status === "rejected"
    );

    if (failures.length > 0) {
      throw new AggregateError(
        failures.map(f => f.reason),
        `${failures.length} upsert(s) failed`
      );
    }
  };

  const upsertItem = async ({
    materialCharacteristics,
    teeth,
    itemId,
  }: CreateItemParams) => {
    // It is easier to delete and re-create than updating
    if (itemId) {
      await deleteItemMutation({ variables: { itemId } });
    }

    const itemToCreate: AnatomyItemCreateInput = {
      id: itemId,
      teeth: teeth,
      ...(materialCharacteristics
        ? {
            itemMaterial: {
              connect: {
                id: materialCharacteristics.material,
              },
            },
          }
        : {}),
      product: {
        connect: {
          id: productId,
        },
      },
      itemType: item.type,
      ...(materialCharacteristics &&
      isPartialMaterialCharacteristicsWithShade(materialCharacteristics)
        ? !isPartialMultiShadeCharacteristics(
            materialCharacteristics.shadeCharacteristics
          )
          ? {
              teethshadeType: "SINGLE_SHADE" as const,
              itemShade: {
                connect: {
                  id: materialCharacteristics.shadeCharacteristics.shade,
                },
              },
            }
          : {
              teethshadeType: "MULTI_SHADE" as const,
              multiShadeInfo: getMultiShadeInfo(
                materialCharacteristics.shadeCharacteristics
              ),
            }
        : {}),
    };
    await createItemMutation({ variables: { data: itemToCreate } });
  };

  const upsertItems = async (items: CreateItemParams[]) => {
    const results = await Promise.allSettled(items.map(upsertItem));

    const failures = results.filter(
      (r): r is PromiseRejectedResult => r.status === "rejected"
    );

    if (failures.length > 0) {
      throw new AggregateError(
        failures.map(f => f.reason),
        `${failures.length} upsert(s) failed`
      );
    }
  };

  return {
    isLoading: isItemMaterialChoicesLoading,
    isDeleting,
    isUpserting: isCreating || isDeleting,
    itemMaterials,
    itemShadeTypes,
    itemShades,
    upsertItem,
    upsertItems,
    deleteItems,
  };
};
